import React from 'react';
import { 
  Users, 
  GraduationCap, 
  MapPin, 
  Network, 
  CheckCircle2, 
  ArrowRight, 
  FileText, 
  Target, 
  BarChart, 
  Globe,
  Heart,
  Church,
  Trophy,
  Building,
  Briefcase
} from 'lucide-react';

const PolicyNowPage: React.FC = () => {
  return (
    <div className="pt-32 pb-20 overflow-hidden">
      {/* HERO SECTION */}
      <section className="px-6 max-w-7xl mx-auto mb-32 relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-blue/10 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="max-w-4xl space-y-8">
          <div className="inline-flex items-center space-x-3 px-4 py-2 glass-island border-brand-blue/30 rounded-full">
            <span className="text-[10px] uppercase tracking-[0.4em] text-brand-blue font-bold">The Activation Engine</span>
          </div>
          <h2 className="text-5xl md:text-[8rem] font-serif italic tracking-tighter leading-[0.85] mb-8 uppercase text-white">
            PolicyNow<span className="text-brand-blue">.</span><br />
            <span className="text-white/40 italic">Activating Engagement.</span>
          </h2>
          <p className="text-2xl text-white font-bold leading-relaxed max-w-2xl">
            A national engagement hub powered by the Boulai platform, designed to bridge the gap between people and the policy that affects them.
          </p>
        </div>
      </section>

      {/* THE BOTTOM-UP STRATEGY */}
      <section className="py-32 px-6 bg-[#0c0c0d] border-y border-white/5 relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-12">
            <div>
              <h3 className="text-3xl font-bold italic mb-6 uppercase tracking-tight text-white">Built to solve adoption.</h3>
              <p className="text-white font-medium leading-relaxed text-lg opacity-60">
                PolicyNow uses a bottom-up deployment approach to build a verified, active user base before the first government contract is even signed. We activate communities where participation is naturally high.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { 
                  icon: <GraduationCap className="text-brand-blue" />, 
                  title: 'Youth & Students', 
                  desc: 'Building the next generation of civic participation in schools and universities.' 
                },
                { 
                  icon: <Heart className="text-brand-blue" />, 
                  title: 'Indigenous Groups', 
                  desc: 'Establishing secure, sovereignty-first channels for land and policy consultation.' 
                },
                { 
                  icon: <Church className="text-brand-blue" />, 
                  title: 'Religious & Community', 
                  desc: 'Partnering with cultural hubs to engage diverse religious and heritage organizations.' 
                },
                { 
                  icon: <Trophy className="text-brand-blue" />, 
                  title: 'Sports & Unions', 
                  desc: 'Activating large member-based organizations, sports associations, and labor unions.' 
                },
                { 
                  icon: <Building className="text-brand-blue" />, 
                  title: 'Gov Agencies', 
                  desc: 'Internal and external alignment for specific government organizations and administrative agencies.' 
                },
                { 
                  icon: <MapPin className="text-brand-blue" />, 
                  title: 'Local Hubs', 
                  desc: 'Direct parent, educator, and non-profit engagement for local and hyper-local governance.' 
                }
              ].map((item, i) => (
                <div key={i} className="space-y-4 group">
                  <div className="w-12 h-12 glass-island rounded-2xl flex items-center justify-center transition-all group-hover:bg-brand-blue/20">
                    {item.icon}
                  </div>
                  <h4 className="text-lg font-bold italic uppercase tracking-tight text-white">{item.title}</h4>
                  <p className="text-sm text-white font-medium leading-relaxed opacity-40">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
             <div className="glass-island p-12 rounded-[4rem] border-white/10 shadow-2xl relative overflow-hidden group">
                <div className="space-y-10 relative z-10">
                   <div className="text-center mb-4">
                      <span className="text-[10px] uppercase tracking-widest text-white/40 font-black">Deployment Flow</span>
                   </div>
                   
                   <div className="flex flex-col items-center">
                      <div className="w-full p-6 glass-island rounded-2xl border-white/5 flex items-center space-x-4">
                         <div className="w-10 h-10 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue"><Users size={22}/></div>
                         <div className="text-left">
                            <span className="block text-xs font-black uppercase tracking-[0.1em] text-white">Community Phase</span>
                            <span className="text-[10px] text-white/40 italic">Onboarding Hubs & Organizations</span>
                         </div>
                      </div>
                      <div className="h-10 w-px bg-gradient-to-b from-brand-blue to-white/5"></div>
                      <div className="w-full p-6 glass-island border-brand-blue/30 bg-brand-blue/5 flex items-center space-x-4 scale-105 shadow-xl shadow-brand-blue/10">
                         <div className="w-10 h-10 bg-brand-blue rounded-xl flex items-center justify-center text-white"><BarChart size={22}/></div>
                         <div className="text-left">
                            <span className="block text-xs font-black uppercase tracking-[0.1em] text-brand-blue">PolicyNow Hub</span>
                            <span className="text-[10px] text-white/80 italic">Active, Verified Participation Loop</span>
                         </div>
                      </div>
                      <div className="h-10 w-px bg-gradient-to-b from-brand-blue to-white/5"></div>
                      <div className="w-full p-6 glass-island rounded-2xl border-white/5 flex items-center space-x-4">
                         <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white"><Globe size={22}/></div>
                         <div className="text-left">
                            <span className="block text-xs font-black uppercase tracking-[0.1em] text-white">Citizen wide adoption</span>
                            <span className="text-[10px] text-white/40 italic">polling, surveying, census, town halls</span>
                         </div>
                      </div>
                      <div className="h-10 w-px bg-gradient-to-b from-white/10 to-transparent"></div>
                      <div className="w-full p-6 glass-island rounded-2xl border-white/5 flex items-center space-x-4 opacity-50">
                         <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white"><CheckCircle2 size={22}/></div>
                         <div className="text-left">
                            <span className="block text-xs font-black uppercase tracking-[0.1em] text-white">Gov Adoption</span>
                            <span className="text-[10px] text-white/40 italic">Platform Handover at scale</span>
                         </div>
                      </div>
                   </div>
                </div>
                <div className="absolute top-0 right-0 p-10 opacity-[0.03] rotate-12 group-hover:rotate-0 transition-transform duration-1000">
                   <FileText size={250} />
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 text-center">
         <div className="max-w-4xl mx-auto px-6">
            <p className="text-brand-blue uppercase tracking-[0.5em] text-[10px] font-black mb-12">Bridging the Gap</p>
            <h3 className="text-4xl md:text-7xl font-serif italic mb-16 uppercase tracking-tighter text-white">People & Policy<span className="text-brand-blue">.</span></h3>
            <p className="text-xl text-white/60 font-bold mb-16 uppercase tracking-widest">Bridging the gap between people and policy that affects them.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <a 
                href="mailto:mdahhan@boulai.net"
                className="px-12 py-6 bg-brand-blue text-white font-black rounded-2xl uppercase text-xs tracking-[0.2em] hover:bg-brand-blue/80 transition-all transform hover:scale-105 active:scale-95 shadow-2xl shadow-brand-blue/30 flex items-center space-x-4"
              >
                <span>Request Walkthrough</span>
                <ArrowRight size={18} />
              </a>
              <a 
                href="https://policynow.ca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-12 py-6 glass-island border-white/10 text-white font-black rounded-2xl uppercase text-xs tracking-[0.2em] hover:bg-white/5 transition-all flex items-center space-x-4"
              >
                <span>PolicyNow.ca</span>
                <Globe size={18} className="text-brand-blue" />
              </a>
            </div>
         </div>
      </section>
    </div>
  );
};

export default PolicyNowPage;